import type { DataType, Unit } from "@soft-db/data-formatter";
import type { HTTPService } from "@soft-db/http";
import type { BatchRecipientsUpdate, Recipients } from "./recipients";
import type { Base64Query } from "./shared";

export type ActiveAlertDataNotification = {
	type: ConditionType;
	limit: string | null;
	startTime: string;
	endTime: string;
	label: string;
	value: string;
	timezone: string;
	trigger: string;
	severity: SeverityLevel;
	saturated: boolean;
	priority: "high" | "low";
	isMain: boolean;
};

export type ActiveAlertNotification = {
	type: "active_alert";
	time: string;
	project: string;
	alert: string;
	severity: SeverityLevel;
	data: ActiveAlertDataNotification[];
};

export type ConditionType = "record" | "vibration_event" | "weekly_limit";
export type ConditionMode = "and" | "or";

export type AlertSummary = {
	id: string;
	name: string;
	enabled: boolean;
	type: ConditionType;
	dataTypes: DataType;
	period: number | null;
	isSubscribed: boolean;
};

export type Scheduler = {
	start: number;
	end: number;
	weekdays: number[];
};

export type RateLimiterMode = "accumulate" | "ignore";
export type UpdateRateLimiter = {
	isDefault: boolean;
	mode: RateLimiterMode;
	period: number;
};
export type RateLimiter = {
	active: boolean;
	resetTime: "2025-09-11T17:46:03.198Z";
} & UpdateRateLimiter;

export type SeverityLevel = "error" | "warning" | "none";

export type ThresholdComparison = "gt" | "lt";

export type CommonCondition = {
	id: string;
	isPrimary: boolean;
	sortOrder: number;
};

export type RecordCondition = {
	type: "record";
	query: Base64Query;
	comparison: ThresholdComparison;
	thresholdError: number | null;
	thresholdWarning: number | null;
} & CommonCondition;

export type WeeklyLimitCondition = {
	type: "weekly_limit";
	query: Base64Query;
	comparison: ThresholdComparison;
	weeklyLimitError: string | null;
	weeklyLimitWarning: string | null;
} & CommonCondition;

export type VibrationEventCondition = {
	type: "vibration_event";
	vibrationCriteriaError: string | null;
	vibrationCriteriaWarning: string | null;
	unit: Unit;
} & CommonCondition;

export type Condition = RecordCondition | VibrationEventCondition | WeeklyLimitCondition;

export type ValidityComparison = "gt" | "lt" | "bt" | "nbt";

export type ValidityCondition = {
	id: string;
	sortOrder: number;
	query: Base64Query;
	period?: number | null;
	silent: boolean;
	comparison: ValidityComparison;
	threshold: number;
	secondThreshold: number | null; // Necessary with bt and nbt or else optional
};

export type UpdateAlert = {
	name: string;
	description: string;
	enabled: boolean;
	type: ConditionType;
	period: number | null;
	conditionMode: ConditionMode;
	conditions: Condition[];
	validityMode: ConditionMode;
	validityConditions: ValidityCondition[];
	scheduler: Scheduler | null;
	recipients: BatchRecipientsUpdate;
	rateLimiter: RateLimiter;
};

export type Alert = {
	id: string;
	recipients: Recipients;
	isSubscribed: boolean;
	site: {
		id: string;
		name: string;
		timezone: string;
	} | null;
} & Omit<UpdateAlert, "recipients">;

export type CreateAlert = {
	name: string;
};

export type RecipientsReport = {
	status: ReportStatus;
	recipients: number;
};

export type SchedulerReport = {
	status: ReportStatus;
	weekdays: boolean;
};

export type RateLimiterReport = {
	status: ReportStatus;
	default: boolean;
};

export type WeeklyLimitReport = {
	status: ReportStatus;
	errorName?: string;
	warningName?: string;
	query?: string;
	exists: boolean;
	period?: number;
};

export type RecordReport = {
	status: ReportStatus;
	query: string;
	exists: boolean;
	period?: number;
	isSmaller: boolean;
};

export type VibrationEventReport = {
	status: ReportStatus;
	errorName?: string;
	warningName?: string;
	exists: boolean;
};

export type ConditionsReport = {
	weeklyLimit: WeeklyLimitReport[];
	record: RecordReport[];
	vibrationEvent: VibrationEventReport[];
};

export enum ReportStatus {
	OK = "ok",
	WARNING = "warning",
	ERROR = "error",
}

export type AlertConfigTestResult = {
	status: ReportStatus;
	enabled: ReportStatus;
	recipients: RecipientsReport;
	rateLimiter: RateLimiterReport;
	scheduler: SchedulerReport | null;
	conditions: ConditionsReport;
	preconditions: RecordReport[];
};

export class AlertApi {
	constructor(private _http: HTTPService) {}

	async fetchSiteAlerts(id: string) {
		return await this._http.get<AlertSummary[]>(`sites/${id}/alerts`);
	}

	async fetch(id: string) {
		return await this._http.get<Alert>(`alerts/${id}`);
	}

	async create(siteId: string, alert: CreateAlert) {
		return await this._http.post<Alert>(`sites/${siteId}/alerts`, alert);
	}

	async update(id: string, update: UpdateAlert) {
		return await this._http.patch<Alert>(`alerts/${id}`, update);
	}

	async delete(id: string) {
		return await this._http.delete(`alerts/${id}`);
	}

	async getAlertNotifications(project: string, range?: [Date, Date]) {
		if (!range) {
			const now = new Date();
			range = [new Date(now.getTime() - 7 * 24 * 60 * 60 * 1_000), now];
		}

		return this._http.get<ActiveAlertNotification[]>(`notifications`, {
			params: {
				publisher: project,
				type: "active_alert",
				from: range[0],
				to: range[1],
			},
		});
	}

	async testReport(id: string) {
		return await this._http.get<AlertConfigTestResult>(`alerts/${id}/test`);
	}
}
